import { getNowBasedOnBooking } from '@lib/booking';
import { DateTime, Settings } from 'luxon';

import { getEarliestStartDateWithOriginalTimezone } from './trip';

import type { PhasedBooking } from '@lib/phased-booking';
import type { Locale } from '@tuic/i18n';
import type { DateTimeFormatOptions, DateTimeUnit } from 'luxon';

export const DATE_OPTION_SHORT_NUMERIC: DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

export const DATE_OPTION_SHORT_NUMERIC_WITHOUT_YEAR: DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
};

export const TIME_OPTION_SHORT: DateTimeFormatOptions = {
  timeStyle: 'short',
};

export const DATE_TIME_OPTION_SHORT_NUMERIC: DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
};

export function toRezonedDateTime(
  date: undefined,
  keepLocalTime?: boolean,
): undefined;

export function toRezonedDateTime(
  date: DateTime | Date | string,
  keepLocalTime?: boolean,
): DateTime;

export function toRezonedDateTime(
  date: DateTime | Date | string | undefined,
  keepLocalTime = false,
): DateTime | undefined {
  if (typeof date === 'undefined') {
    return undefined;
  }

  if (typeof date === 'string') {
    date = DateTime.fromISO(date, {
      setZone: true,
    });
  }

  if (!('year' in date)) {
    date = DateTime.fromJSDate(date);
  }

  return date.setZone(Settings.defaultZone, { keepLocalTime });
}

/**
 * Returns a localized datetime expression where the timezone differences are ignored.
 */
export const toLocalizedExpressionIgnoringTimezone = (
  from: DateTime | Date | string | undefined,
  locale: Locale,
  options: Intl.DateTimeFormatOptions,
): string | undefined => {
  if (typeof from === 'undefined') {
    return undefined;
  }

  return toRezonedDateTime(from, true).toLocaleString(options, { locale });
};

export const datesAreSameYear = (
  ...args: (string | DateTime | Date | undefined)[]
): boolean => {
  const years = args
    .filter((date): date is NonNullable<typeof date> => {
      return typeof date !== 'undefined';
    })
    .map(date => {
      if (typeof date === 'string') {
        date = DateTime.fromISO(date);
      }

      if (!('year' in date)) {
        date = DateTime.fromJSDate(date);
      }

      return date.year;
    });

  const min = Math.min(...years);
  const max = Math.max(...years);

  return min === max;
};

export const getTripDatesExpressions = (
  locale: Locale,
  dateFromArg: Date | DateTime | string | undefined,
  dateToArg: Date | DateTime | string | undefined,
  ignoreTimezone = false,
) => {
  const dateFrom = dateFromArg
    ? toRezonedDateTime(dateFromArg, ignoreTimezone)
    : undefined;
  const dateTo = dateToArg
    ? toRezonedDateTime(dateToArg, ignoreTimezone)
    : undefined;

  const sameYear = datesAreSameYear(dateFrom, dateTo);

  const dateFromExpression = toLocalizedExpressionIgnoringTimezone(
    dateFrom,
    locale,
    sameYear
      ? DATE_OPTION_SHORT_NUMERIC_WITHOUT_YEAR
      : DATE_OPTION_SHORT_NUMERIC,
  );

  if (typeof dateFromExpression === 'undefined') {
    return [undefined, undefined];
  }

  const dateToExpression = toLocalizedExpressionIgnoringTimezone(
    dateTo,
    locale,
    DATE_OPTION_SHORT_NUMERIC,
  );

  if (typeof dateToExpression === 'undefined') {
    return [dateFromExpression, undefined];
  }

  return [dateFromExpression, dateToExpression];
};

export const getTripDateRangeExpression = (
  locale: Locale,
  dateFrom: DateTime | Date | string | undefined,
  dateTo: DateTime | Date | string | undefined,
  ignoreTimezone = false,
) => {
  const [from, to] = getTripDatesExpressions(
    locale,
    dateFrom,
    dateTo,
    ignoreTimezone,
  );

  if (typeof from === 'undefined') {
    return undefined;
  }

  if (typeof to === 'undefined') {
    return from;
  }

  return `${from} – ${to}`;
};

/**
 * Returns the number of nights between `from` and `to`.
 */
export const getNightsBetween = (from: string, to: string): number => {
  const fromDate = DateTime.fromISO(from, { setZone: true }).toISODate();
  const toDate = DateTime.fromISO(to, { setZone: true }).toISODate();

  if (!fromDate || !toDate) {
    // One of the dates is invalid, we can't calculate the nights, so we return 0 as fallback.
    return 0;
  }

  return DateTime.fromISO(toDate).diff(DateTime.fromISO(fromDate), 'days').days;
};

/**
 * Returns the current day of a trip, anchored to the start port's wall clock.
 *
 * Both operands are evaluated in the offset embedded in the trip's
 * startDateTime ISO (e.g. +08:00 for a cruise starting in Hong Kong) so that
 * "Day N" counts from the ship's local midnight, not the device's.
 */
export const getDayOfTrip = (booking: PhasedBooking): number | undefined => {
  const dateFrom = getEarliestStartDateWithOriginalTimezone(booking);

  if (typeof dateFrom === 'undefined') {
    return undefined;
  }

  const now = getNowBasedOnBooking(booking).setZone(dateFrom.zoneName);

  const day =
    now.startOf('day').diff(dateFrom.startOf('day'), 'days').as('days') + 1;

  return day > 0 ? day : undefined;
};

/**
 * Compares two dates and returns true if they are equal within the given precision.
 */
export const isDateEquals = (
  a: DateTime | Date | string,
  b: DateTime | Date | string,
  precision: DateTimeUnit = 'millisecond',
): boolean => {
  const aAsDate = toRezonedDateTime(a);
  const bAsDate = toRezonedDateTime(b);

  return aAsDate.startOf(precision).equals(bAsDate.startOf(precision));
};
